import { createContext, useContext } from "react";
import type { DrillTarget } from "../../lib/drill/targets";

/**
 * The open drill panels — docs/24 §7.8.
 *
 * |                     | Drill panel                         | Dialog            |
 * |---------------------|-------------------------------------|-------------------|
 * | What it does        | Reads one record                    | Writes one record |
 * | How many at once    | Several, keyed by kind-and-id       | One               |
 * | How it opens        | Double-click a row, or the row menu | A named button    |
 * | Survives navigation | No — cleared on a route change      | n/a               |
 *
 * Several panels may be open at once because the question a reader has about a
 * READ is comparative. A WRITE is the opposite: a second draft over one record
 * is somebody losing work they cannot see, so every write in this console is a
 * single dialog and does not come through here.
 *
 * Failure mode in both directions: a write that stacks is two forms over one
 * record, and the loser is whichever the operator submits second — silently,
 * because neither panel can see the other. A read that does not stack refuses to
 * hold two records side by side, which is the whole question.
 */

export interface OpenPanel extends DrillTarget {
  readonly key: string;
}

export interface DrillValue {
  readonly panels: readonly OpenPanel[];
  readonly open: (target: DrillTarget) => void;
  readonly close: (key: string) => void;
}

export const DrillContext = createContext<DrillValue | null>(null);

export function keyOf(target: DrillTarget): string {
  return `${target.kind}:${target.id}`;
}

/**
 * §7.2: a grid drills by spreading this hook with **no declaration at all**. A
 * per-screen declaration is a per-screen chance to forget — the last engine of
 * this shape landed on four screens because four is how many somebody remembered
 * to wrap.
 *
 * Outside a provider it is inert rather than throwing: a register rendered in a
 * test or inside a picker's window is still a register, and a hard failure there
 * would be an engine that refuses to be reused.
 */
export function useDrill(): DrillValue {
  return useContext(DrillContext) ?? INERT;
}

const INERT: DrillValue = { panels: [], open: () => {}, close: () => {} };
